import { getMacAddressError, normalizeMacAddress } from '@emma/core/device';
import type { InferResponseType } from 'hono/client';
import { useCallback, useState } from 'react';
import { type ApiClient, getApiErrorMessage, useMutation } from '@/hooks/api';
import { useCanOperateById } from '@/hooks/workspace';

type CreateDevice = ApiClient['v5']['devices']['$post'];
type CreateDeviceResponse = InferResponseType<CreateDevice, 200>;

export type NetworkRow = { id: string; interface: string; macAddress: string };

type Options = {
  initialWorkspaceId?: number;
  initialVendor?: string;
  initialModel?: string;
  initialSerial?: string;
  initialNetworks?: NetworkRow[];
};

export function useDeviceCreationForm(options: Options = {}) {
  const [workspaceId, setWorkspaceId] = useState<number | undefined>(options.initialWorkspaceId);
  const [vendor, setVendor] = useState<string | undefined>(options.initialVendor);
  const [model, setModel] = useState<string | undefined>(options.initialModel);
  const [serial, setSerial] = useState(options.initialSerial ?? '');
  const [networks, setNetworks] = useState<NetworkRow[]>(
    options.initialNetworks?.length
      ? options.initialNetworks
      : [{ id: crypto.randomUUID(), interface: '', macAddress: '' }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const changeVendor = useCallback((newVendor: string | undefined) => {
    setVendor(newVendor);
    setModel(undefined);
  }, []);

  const canOperate = useCanOperateById(workspaceId);

  const submitMutation = useMutation<CreateDeviceResponse, number>(
    'create-device',
    (client, submitWorkspaceId) => {
      const registrationFile = {
        vendor,
        devices: [
          {
            serial,
            model,
            network: networks.map((n) => ({
              macAddress: normalizeMacAddress(n.macAddress),
            })),
          },
        ],
      };

      return client.v5.devices.$post({
        json: {
          workspaceId: submitWorkspaceId,
          registrationFile: btoa(JSON.stringify(registrationFile)),
        },
      });
    },
    (err) => {
      setError(getApiErrorMessage(err, 'Failed to create device'));
    },
    { suppressToast: true }
  );

  const serialTooLong = serial.length > 100;
  const macValidationErrors = networks.map((n) => getMacAddressError(n.macAddress));
  const hasMacErrors = macValidationErrors.some(Boolean);
  const hasAtLeastOneMac = networks.some((n) => n.macAddress.trim().length > 0);

  const canSubmit =
    canOperate &&
    !!workspaceId &&
    !!vendor &&
    !!model &&
    serial.trim().length > 0 &&
    !serialTooLong &&
    !hasMacErrors &&
    hasAtLeastOneMac &&
    !isSubmitting;

  const handleSubmit = async (): Promise<number | undefined> => {
    if (!canSubmit || !workspaceId) return undefined;
    setIsSubmitting(true);
    setError(undefined);
    try {
      const response = await submitMutation(workspaceId);
      return response?.devices?.[0]?.id;
    } catch {
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = useCallback((overrides?: Partial<Options>) => {
    setWorkspaceId(overrides?.initialWorkspaceId);
    setVendor(overrides?.initialVendor);
    setModel(overrides?.initialModel);
    setSerial(overrides?.initialSerial ?? '');
    setNetworks(
      overrides?.initialNetworks?.length
        ? overrides.initialNetworks
        : [{ id: crypto.randomUUID(), interface: '', macAddress: '' }]
    );
    setError(undefined);
  }, []);

  return {
    workspaceId,
    setWorkspaceId,
    vendor,
    setVendor,
    changeVendor,
    model,
    setModel,
    serial,
    setSerial,
    networks,
    setNetworks,
    isSubmitting,
    error,
    serialTooLong,
    macValidationErrors,
    hasMacErrors,
    hasAtLeastOneMac,
    canSubmit,
    handleSubmit,
    resetForm,
  };
}
